import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';
import {
  GraphResponse,
  GraphId,
  GraphNoteId,
  GraphRequestParams,
  GraphValueId,
  GraphPageQuery,
  GraphDailyQuotaId,
  GraphOverlayQuery,
  GraphPdfQuery,
  GraphSortQuery,
} from '@business/entities/graph';
import { GraphAPI } from '@business/api/graph';
import { GraphValueAPI } from '@business/api/graph_value';
import { GraphValueFormData } from '@modules/statistics/graphs/AddEditGraphValueModal/AddEditGraphValueModal';
import { GraphNoteAPI } from '@business/api/graph_note';
import { GraphNoteFormData } from '@modules/statistics/graphs/EditGraphNoteModal/EditGraphNoteModal';
import { GraphDailyQuotaAPI } from '@business/api/graph_quota';
import { GraphQuotaFormData } from '@modules/statistics/graphs/AddEditDailyQuotaGraphModal/AddEditDailyQuotaGraphModal';
import { BehaviorSubject } from 'rxjs';
import { GroupFormData } from '@modules/statistics/groups/AddEditGroupModal/AddEditGroupModal';

export class GraphBloc extends BaseBloc {
  private readonly graphRepo = new Repository<GraphResponse>({
    getItemId: item => item.id,
  });

  readonly graphs$ = this.graphRepo.items$;
  readonly graphs = this.graphRepo.items;
  readonly selectGraph = this.graphRepo.selectItem;
  readonly dateRange$ = new BehaviorSubject<string | null>(null);

  onReset() {
    this.graphRepo.reset();
  }

  createGraph = (data: Partial<GraphResponse>) => {
    return GraphAPI.createGraph(data).pipe(
      this.graphRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_graph'),
      }),
    );
  };

  updateGraph = (id: GraphId, data: Partial<GraphResponse>) => {
    return GraphAPI.updateGraph(id, data).pipe(
      this.graphRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_graph'),
      }),
    );
  };

  updateArchiveGraph = (id: GraphId, data: Partial<GraphResponse>) => {
    return GraphAPI.updateArchiveGraph(id, data).pipe(
      this.graphRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_graph'),
      }),
    );
  };

  syncValuesGraph = (id: GraphId) => {
    return GraphAPI.syncValuesGraph(id).pipe(
      this.graphRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_graph'),
      }),
    );
  };

  fetchGraphs = (params?: GraphRequestParams) => {
    return GraphAPI.fetchGraphs(params).pipe(this.graphRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_graph_error') }));
  };

  onScrollFetchGraphs = (params?: GraphRequestParams) => {
    return GraphAPI.fetchGraphs(params).pipe(
      this.graphRepo.ops.upsertMany(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_graph_error') }),
    );
  };

  fetchFilterGraphs = (params?: GraphRequestParams) => {
    return GraphAPI.fetchFilterGraphs(params).pipe(this.graphRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_graph_error') }));
  };

  fetchGraphsType = (params?: GraphRequestParams) => {
    return GraphAPI.fetchGraphsType(params).pipe(this.graphRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_graph_error') }));
  };

  fetchGraphType = (params?: GraphRequestParams) => {
    return GraphAPI.fetchGraphType(params).pipe(
      this.graphRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_graph_error') }),
    );
  };

  fetchFilteredGraphs = (params?: GraphOverlayQuery) => {
    return GraphAPI.fetchFilteredGraphs(params).pipe(this.graphRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_graph_error') }));
  };

  createGraphValue = (data: GraphValueFormData) => {
    return GraphValueAPI.createValue(data).pipe(
      this.graphRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('create_graph_value'),
      }),
    );
  };

  updateGraphValue = (id: string, data: GraphValueFormData) => {
    return GraphValueAPI.updateValue(id, data).pipe(
      this.graphRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('create_graph_value'),
      }),
    );
  };

  // fetchGraph = (id: GraphId, params?: GraphPageQuery) => {
  //   return GraphAPI.fetchGraph(id, params).pipe(handleMessage({ error: requestMessage('fetch_graph_error') }));
  // };

  fetchGraph = (id: string, params?: GraphPageQuery) => {
    return GraphAPI.fetchGraph(id, params).pipe(
      this.graphRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_graph_error') }),
    );
  };

  fetchGraphValue = (id: GraphId) => {
    return GraphValueAPI.fetchValue(id).pipe(handleMessage({ error: requestMessage('fetch_graph_error') }));
  };

  createGraphNote = (data: GraphNoteFormData) => {
    return GraphNoteAPI.createNote(data).pipe(
      this.graphRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('create_graph_value'),
      }),
    );
  };

  updateGraphNote = (id: GraphNoteId, data: GraphNoteFormData) => {
    return GraphNoteAPI.updateNote(id, data).pipe(
      this.graphRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('create_graph_value'),
      }),
    );
  };

  deleteGraphNote = (id: GraphNoteId) => {
    return showDeleteConfirmation('Delete Note', 'Do you really want to remove this note?').pipe(
      switchMap(() => GraphNoteAPI.deleteNote(id)),
      this.graphRepo.ops.upsertOne(item => ({ item })),

      handleMessage({
        type: requestMessage('delete_battle_plan_section'),
      }),
    );
  };

  deleteGraphValue = (id: GraphValueId) => {
    return showDeleteConfirmation('Delete Value', 'Do you really want to delete this value?').pipe(
      switchMap(() => GraphValueAPI.deleteValue(id)),

      handleMessage({
        type: requestMessage('delete_graph_value'),
      }),
    );
  };

  updateGraphsSort = (data: GraphSortQuery) => {
    return GraphAPI.updateGraphsSort(data).pipe(
      handleMessage({
        type: requestMessage('graph_sort'),
      }),
    );
  };

  updateAllGraphsSort = (data: GraphSortQuery) => {
    return GraphAPI.updateAllGraphsSort(data).pipe(
      handleMessage({
        type: requestMessage('graph_sort'),
      }),
    );
  };

  fetchGraphDailyQuota = (id: GraphDailyQuotaId, week: number, year: number) => {
    return GraphDailyQuotaAPI.fetchDailyQuota(id, week, year).pipe(handleMessage({ error: requestMessage('fetch_graph_error') }));
  };

  createGraphDailyQuota = (data: GraphQuotaFormData) => {
    return GraphDailyQuotaAPI.createDailyQuota(data).pipe(
      this.graphRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('create_graph_value'),
      }),
    );
  };

  updateGraphDailyQuota = (id: GraphDailyQuotaId, data: GraphQuotaFormData) => {
    return GraphDailyQuotaAPI.updateDailyQuota(id, data).pipe(
      this.graphRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('create_graph_value'),
      }),
    );
  };

  fetchGraphsOverlay = (id: GraphId) => {
    return GraphAPI.fetchGraphsOverlay(id).pipe(handleMessage({ error: requestMessage('fetch_graph_error') }));
  };

  fetchOverlayGraph = (id: GraphId, data: GraphOverlayQuery) => {
    return GraphAPI.fetchOverlayGraphDetails(id, data).pipe(
      this.graphRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_graph_error') }),
    );
  };

  fetchGraphsPdf = (params?: GraphRequestParams) => {
    return GraphAPI.fetchGraphsPdf(params).pipe(this.graphRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_graph_error') }));
  };

  fetchGraphPdf = (id: string) => {
    return GraphAPI.fetchGraphPdf(id).pipe(
      this.graphRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_graph_error') }),
    );
  };

  generatePdf = (data: GraphPdfQuery) => {
    return GraphAPI.generatePdf(data).pipe(handleMessage({ error: requestMessage('fetch_graph_pdf_error') }));
  };

  generatePdfs = (data: GraphPdfQuery) => {
    return GraphAPI.generatePdfs(data).pipe(handleMessage({ error: requestMessage('fetch_graph_pdf_error') }));
  };

  updateGraphGroup = (data: GroupFormData) => {
    return GraphAPI.updateGraphGroup(data).pipe(
      handleMessage({
        type: requestMessage('create_graph_value'),
      }),
    );
  };
}
